'use client';

export const dynamic = 'force-dynamic';

import React, { useState, useEffect, useMemo, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams, useRouter } from 'next/navigation';
import { Loader2, Download, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, Search, Lock } from 'lucide-react';
import { getIPOs } from '@/lib/api-client';
import type { IPO } from '@/lib/api-client';
import { formatCurrency, formatPct, formatMarketCap, cn } from '@/lib/utils';
import { isUpcomingIpo, todayYmdInTz } from '@/lib/ipoFilters';
import { useTimezone } from '@/hooks/useTimezone';
import { getETDate, addEtDays } from '@/lib/marketTime';
import { useFeatures } from '@/context/SiteContext';

type TabType = 'upcoming' | 'recent' | 'calendar';
type SortDir = 'asc' | 'desc';

function safeDate(dateStr: string | null | undefined, opts: Intl.DateTimeFormatOptions, tz?: string): string {
  if (!dateStr) return 'TBD';
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d.getTime())) return 'TBD';
  return d.toLocaleDateString('en-US', { ...opts, timeZone: tz });
}

const PAGE_SIZE = 25;

const STATUS_STYLES: Record<string, string> = {
  Priced:    'bg-green-500/15 text-green-400',
  Scheduled: 'bg-sky-500/15 text-sky-400',
  Filed:     'bg-indigo-500/15 text-indigo-400',
  Postponed: 'bg-amber-500/15 text-amber-400',
  Withdrawn: 'bg-red-500/15 text-red-400',
};

const STATUS_DOT: Record<string, string> = {
  Priced:    'bg-green-400',
  Scheduled: 'bg-sky-400',
  Filed:     'bg-indigo-400',
  Postponed: 'bg-amber-400',
  Withdrawn: 'bg-red-400',
};

// ── CSV export ─────────────────────────────────────────────────────────────────

function exportCSV(data: IPO[], tab: TabType) {
  const headers = tab === 'upcoming'
    ? ['Expected Date', 'Company', 'Symbol', 'Exchange', 'Sector', 'Price Range', 'Deal Size', 'Status']
    : ['IPO Date', 'Symbol', 'Company', 'Exchange', 'Sector', 'IPO Price', 'Current Price', 'Return %', 'Deal Size'];
  const rows = data.map((ipo) => {
    // Filed deals carry a (past) filing date, not an expected date — show TBD.
    const date = tab === 'upcoming' && ipo.status === 'Filed'
      ? 'TBD'
      : safeDate(ipo.ipoDate, { month: 'short', day: 'numeric', year: 'numeric' });
    const priceRange = ipo.priceRangeLow && ipo.priceRangeHigh ? `$${ipo.priceRangeLow}-$${ipo.priceRangeHigh}` : '';
    if (tab === 'upcoming') return [date, ipo.company, ipo.ticker, ipo.exchange ?? '', ipo.sector ?? '', priceRange, ipo.size ?? '', ipo.status];
    return [date, ipo.ticker, ipo.company, ipo.exchange ?? '', ipo.sector ?? '', priceRange, ipo.currentPrice ?? '', ipo.returnSinceIpo ?? '', ipo.size ?? ''];
  });
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `ipos-${tab}-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
}

// ── Sort header ────────────────────────────────────────────────────────────────

function SortTh({ label, sortKey, active, dir, onSort }: {
  label: string; sortKey: string; active: string; dir: SortDir; onSort: (k: string) => void;
}) {
  const isActive = active === sortKey;
  return (
    <th className="px-4 py-2.5 text-left text-xs font-medium text-fg-subtle uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-fg transition-colors" onClick={() => onSort(sortKey)}>
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (dir === 'asc' ? <ChevronUp size={12} className="text-sky-400" /> : <ChevronDown size={12} className="text-sky-400" />) : <ChevronsUpDown size={12} className="text-slate-600" />}
      </div>
    </th>
  );
}

// ── Filter bar ─────────────────────────────────────────────────────────────────

function FilterBar({ sectors, exchanges, statuses, filterSector, filterExchange, filterStatus, search, onChange, onSearch }: {
  sectors: string[]; exchanges: string[]; statuses: string[];
  filterSector: string; filterExchange: string; filterStatus: string;
  search: string;
  onChange: (key: 'sector' | 'exchange' | 'status', value: string) => void;
  onSearch: (q: string) => void;
}) {
  const sel = 'bg-card-bg border border-default text-slate-300 text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-sky-500 transition-colors';
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
        <input
          type="text"
          placeholder="Search company or ticker…"
          value={search}
          onChange={e => onSearch(e.target.value)}
          className="bg-card-bg border border-default rounded-lg pl-8 pr-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-500 transition-colors w-52"
        />
      </div>
      {statuses.length > 1 && (
        <select value={filterStatus} onChange={e => onChange('status', e.target.value)} className={sel}>
          <option value="all">All Statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      {sectors.length > 0 && (
        <select value={filterSector} onChange={e => onChange('sector', e.target.value)} className={sel}>
          <option value="all">All Sectors</option>
          {sectors.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      {exchanges.length > 0 && (
        <select value={filterExchange} onChange={e => onChange('exchange', e.target.value)} className={sel}>
          <option value="all">All Exchanges</option>
          {exchanges.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
      )}
    </div>
  );
}

// ── Pagination ─────────────────────────────────────────────────────────────────

function Pagination({ page, totalPages, total, pageSize, onPage }: {
  page: number; totalPages: number; total: number; pageSize: number; onPage: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 px-1">
      <span className="text-xs text-fg-subtle">Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(page - 1)} disabled={page === 1} className="px-2.5 py-1 text-xs bg-card-hover-bg border border-default text-slate-300 rounded-lg disabled:opacity-40 hover:bg-border-default transition-colors">← Prev</button>
        <span className="text-xs text-fg-muted px-2">Page {page} of {totalPages}</span>
        <button onClick={() => onPage(page + 1)} disabled={page === totalPages} className="px-2.5 py-1 text-xs bg-card-hover-bg border border-default text-slate-300 rounded-lg disabled:opacity-40 hover:bg-border-default transition-colors">Next →</button>
      </div>
    </div>
  );
}

// ── Compact date strip (horizontal scrolling calendar above upcoming table) ───

function CompactDateStrip({ ipos }: { ipos: IPO[] }) {
  // Build next 21 days from today's ET date — a US-market IPO calendar is
  // ET-anchored; the old toISOString()-from-now started a day late in the
  // US evening / for non-ET users.
  const todayStr = getETDate(new Date());
  const days: string[] = [];
  for (let i = 0; i < 21; i++) days.push(addEtDays(todayStr, i));

  // Count IPOs per day
  const countByDate: Record<string, number> = {};
  for (const ipo of ipos) {
    if (!ipo.ipoDate) continue;
    const key = ipo.ipoDate.slice(0, 10);
    countByDate[key] = (countByDate[key] ?? 0) + 1;
  }

  return (
    <div className="bg-card-bg border border-default rounded-xl p-3 mb-4 overflow-hidden">
      <div className="flex gap-1.5 overflow-x-auto scrollbar-thin pb-1">
        {days.map((dateStr) => {
          // Fixed ET calendar date — anchor + render in UTC so labels are the date itself.
          const d = new Date(dateStr + 'T12:00:00Z');
          const dayName = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
          const dayNum = d.getUTCDate();
          const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
          const count = countByDate[dateStr] ?? 0;
          const isToday = dateStr === todayStr;
          const isWeekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;

          return (
            <div
              key={dateStr}
              className={cn(
                'flex flex-col items-center min-w-[52px] px-2 py-2 rounded-lg text-center flex-shrink-0 transition-colors',
                isToday ? 'bg-sky-500/15 border border-sky-500/30' :
                count > 0 ? 'bg-card-hover-bg border border-default' :
                isWeekend ? 'opacity-40' : 'border border-transparent',
              )}
            >
              <span className={cn('text-[10px] font-medium uppercase', isToday ? 'text-sky-400' : 'text-fg-subtle')}>{dayName}</span>
              <span className={cn('text-sm font-bold', isToday ? 'text-sky-300' : count > 0 ? 'text-white' : 'text-slate-600')}>{dayNum}</span>
              <span className="text-[9px] text-slate-600">{month}</span>
              {count > 0 && (
                <span className={cn(
                  'mt-1 w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold',
                  isToday ? 'bg-sky-500 text-white' : 'bg-indigo-500/20 text-indigo-400',
                )}>
                  {count}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Calendar view ──────────────────────────────────────────────────────────────

function IPOCalendar({ ipos }: { ipos: IPO[] }) {
  const tz = useTimezone();
  const [year, setYear] = useState(() => parseInt(getETDate(new Date()).slice(0, 4), 10));
  const [month, setMonth] = useState(() => parseInt(getETDate(new Date()).slice(5, 7), 10) - 1); // 0-indexed (ET)
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const monthStart = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDow = monthStart.getDay(); // 0=Sun

  // Build IPO map keyed by date string
  const ipoByDate = useMemo(() => {
    const map: Record<string, IPO[]> = {};
    for (const ipo of ipos) {
      if (!ipo.ipoDate) continue;
      const key = ipo.ipoDate.slice(0, 10);
      if (!key || key.length < 10) continue;
      if (!map[key]) map[key] = [];
      map[key].push(ipo);
    }
    return map;
  }, [ipos]);

  const todayStr = getETDate(new Date());

  const navMonth = (delta: number) => {
    let m = month + delta;
    let y = year;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMonth(m); setYear(y); setSelectedDate(null);
  };

  const monthLabel = new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: tz });

  // All days including leading blanks
  const cells: (number | null)[] = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const selectedIpos = selectedDate ? (ipoByDate[selectedDate] ?? []) : [];
  const monthHasIpos = Object.keys(ipoByDate).some(k => k.startsWith(`${year}-${String(month + 1).padStart(2, '0')}`));

  return (
    <div className="space-y-4">
      {/* Month navigator */}
      <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-default">
          <button onClick={() => navMonth(-1)} className="p-1.5 rounded-lg hover:bg-card-hover-bg text-fg-muted hover:text-white transition-colors">
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-semibold text-white">{monthLabel}</span>
          <button onClick={() => navMonth(1)} className="p-1.5 rounded-lg hover:bg-card-hover-bg text-fg-muted hover:text-white transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 border-b border-default">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="py-2 text-center text-[11px] font-medium text-fg-subtle uppercase tracking-wide">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            if (day === null) return <div key={`blank-${i}`} className="min-h-[72px] border-b border-r border-card-hover-bg/50 last:border-r-0" />;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayIpos = ipoByDate[dateStr] ?? [];
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === selectedDate;
            const isWeekend = [0, 6].includes(i % 7);

            return (
              <button
                key={dateStr}
                onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                className={cn(
                  'min-h-[72px] p-1.5 text-left border-b border-r border-card-hover-bg/50 transition-colors relative',
                  (i + 1) % 7 === 0 && 'border-r-0',
                  isSelected ? 'bg-sky-500/10' : isWeekend ? 'bg-surface/40 hover:bg-card-hover-bg/40' : 'hover:bg-card-hover-bg/50',
                )}
              >
                <div className={cn(
                  'w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium mb-1',
                  isToday ? 'bg-sky-500 text-white' : isSelected ? 'text-sky-400 font-semibold' : 'text-fg-muted',
                )}>
                  {day}
                </div>
                <div className="space-y-0.5">
                  {dayIpos.slice(0, 2).map(ipo => (
                    <div key={ipo.id} className="flex items-center gap-1">
                      <div className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', STATUS_DOT[ipo.status] ?? 'bg-slate-500')} />
                      <span className="text-[10px] text-slate-300 font-mono truncate leading-tight">{ipo.ticker}</span>
                    </div>
                  ))}
                  {dayIpos.length > 2 && (
                    <div className="text-[10px] text-fg-subtle">+{dayIpos.length - 2} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {!monthHasIpos && (
          <div className="py-6 text-center text-sm text-fg-subtle border-t border-default">No IPOs scheduled for {monthLabel}</div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 px-1">
        {Object.entries(STATUS_DOT).map(([status, dot]) => (
          <div key={status} className="flex items-center gap-1.5">
            <div className={cn('w-2 h-2 rounded-full', dot)} />
            <span className="text-xs text-fg-subtle">{status}</span>
          </div>
        ))}
      </div>

      {/* Selected date detail */}
      {selectedDate && (
        <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-default">
            <span className="text-sm font-semibold text-white">
              {safeDate(selectedDate, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }, tz)}
            </span>
            <span className="ml-2 text-xs text-fg-subtle">{selectedIpos.length} IPO{selectedIpos.length !== 1 ? 's' : ''}</span>
          </div>
          {selectedIpos.length === 0 ? (
            <div className="px-5 py-6 text-sm text-fg-subtle text-center">No IPOs on this date.</div>
          ) : (
            <div className="divide-y divide-border-default/50">
              {selectedIpos.map(ipo => (
                <div key={ipo.id} className="px-5 py-3 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-bold font-mono text-sky-400 flex-shrink-0">{ipo.ticker}</span>
                    <span className="text-sm text-slate-300 truncate">{ipo.company}</span>
                    {ipo.exchange && <span className="text-xs text-slate-600 flex-shrink-0">{ipo.exchange}</span>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {ipo.priceRangeLow && ipo.priceRangeHigh && (
                      <span className="text-xs font-mono text-fg-muted">${ipo.priceRangeLow}–${ipo.priceRangeHigh}</span>
                    )}
                    {ipo.size && <span className="text-xs font-mono text-fg-muted">{formatMarketCap(ipo.size)}</span>}
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', STATUS_STYLES[ipo.status] ?? 'bg-slate-700 text-fg-muted')}>{ipo.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

function IPOsPageInner() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const tz = useTimezone();
  const features = useFeatures();

  const getTabFromParam = (): TabType => {
    const p = searchParams.get('tab');
    if (p === 'recent' || p === 'upcoming' || p === 'calendar') return p;
    return 'upcoming';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getTabFromParam);
  const [ipos, setIpos] = useState<IPO[]>([]);
  const [loading, setLoading] = useState(true);

  // Sync tab when URL param changes (e.g. nav link clicks)
  useEffect(() => {
    setActiveTab(getTabFromParam());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSector, setFilterSector] = useState('all');
  const [filterExchange, setFilterExchange] = useState('all');
  const [search, setSearch] = useState('');

  // Sort
  const [sortKey, setSortKey] = useState('ipoDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Pagination
  const [page, setPage] = useState(1);

  useEffect(() => {
    getIPOs().then(setIpos).catch(() => setIpos([])).finally(() => setLoading(false));
  }, []);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setFilterStatus('all'); setFilterSector('all'); setFilterExchange('all'); setSearch('');
    setSortKey('ipoDate'); setSortDir('desc');
    setPage(1);
    router.replace(`/ipos?tab=${tab}`, { scroll: false });
  };

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const handleFilter = (key: 'sector' | 'exchange' | 'status', value: string) => {
    if (key === 'sector') setFilterSector(value);
    else if (key === 'exchange') setFilterExchange(value);
    else setFilterStatus(value);
    setPage(1);
  };

  const todayYmd = useMemo(() => todayYmdInTz(tz), [tz]);
  const recent   = useMemo(() => ipos.filter(i => i.status === 'Priced'), [ipos]);
  const upcoming = useMemo(() => ipos.filter(i => isUpcomingIpo(i, todayYmd)), [ipos, todayYmd]);

  const baseData = activeTab === 'upcoming' ? upcoming : recent;

  const sectors   = useMemo(() => [...new Set(baseData.map(i => i.sector).filter(Boolean))].sort() as string[], [baseData]);
  const exchanges = useMemo(() => [...new Set(baseData.map(i => i.exchange).filter(Boolean))].sort() as string[], [baseData]);
  const statuses  = useMemo(() => [...new Set(baseData.map(i => i.status).filter(Boolean))].sort() as string[], [baseData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return baseData.filter(ipo => {
      if (filterStatus !== 'all' && ipo.status !== filterStatus) return false;
      if (filterSector !== 'all' && ipo.sector !== filterSector) return false;
      if (filterExchange !== 'all' && ipo.exchange !== filterExchange) return false;
      if (q && !ipo.company.toLowerCase().includes(q) && !ipo.ticker.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [baseData, filterStatus, filterSector, filterExchange, search]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const av = a[sortKey as keyof IPO] ?? '';
    const bv = b[sortKey as keyof IPO] ?? '';
    if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  }), [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const sortProps = { active: sortKey, dir: sortDir, onSort: handleSort };

  const tabs: [TabType, string][] = [
    ['upcoming', `Upcoming${!loading ? ` (${upcoming.length})` : ''}`],
    ['recent',   `Recent / Priced${!loading ? ` (${recent.length})` : ''}`],
    ['calendar', 'Calendar'],
  ];

  const showExport = !loading && sorted.length > 0 && activeTab !== 'calendar';

  return (
    <div className="px-2 py-6">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">IPO Center</h1>
          <p className="text-fg-muted text-sm mt-1">Track recent and upcoming initial public offerings</p>
        </div>
        {showExport && (features.exportData ? (
          <button onClick={() => exportCSV(sorted, activeTab)} className="flex items-center gap-1.5 px-3 py-1.5 bg-card-bg hover:bg-card-hover-bg border border-default text-slate-300 hover:text-white text-xs font-medium rounded-lg transition-colors">
            <Download size={13} /> Export CSV
          </button>
        ) : (
          <div className="relative group">
            <button disabled className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card-bg border border-default text-xs text-slate-600 cursor-not-allowed">
              <Lock size={11} className="text-amber-500/70" /> Export CSV
            </button>
            <div className="absolute right-0 top-full mt-1.5 w-48 bg-card-bg border border-default rounded-lg px-3 py-2 text-xs text-fg-muted shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
              <span className="text-amber-400 font-medium">Pro feature</span> — upgrade to unlock CSV export.
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-default">
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => handleTabChange(id)} className={cn('px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors', activeTab === id ? 'text-sky-400 border-sky-400' : 'text-fg-muted hover:text-white border-transparent')}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 size={24} className="animate-spin text-sky-400" /></div>
      ) : activeTab === 'calendar' ? (
        <IPOCalendar ipos={ipos} />
      ) : activeTab === 'upcoming' ? (
        upcoming.length === 0 ? (
          <div className="bg-card-bg border border-default rounded-xl px-6 py-16 text-center">
            <p className="text-fg-muted">No upcoming IPOs available from the current data provider.</p>
          </div>
        ) : (
          <>
            {/* Compact date strip */}
            <CompactDateStrip ipos={upcoming} />
            <FilterBar sectors={sectors} exchanges={exchanges} statuses={statuses} filterSector={filterSector} filterExchange={filterExchange} filterStatus={filterStatus} search={search} onChange={handleFilter} onSearch={q => { setSearch(q); setPage(1); }} />
            <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-default bg-card-hover-bg/50">
                    <tr>
                      <SortTh label="Expected Date" sortKey="ipoDate" {...sortProps} />
                      <SortTh label="Company" sortKey="company" {...sortProps} />
                      <SortTh label="Symbol" sortKey="ticker" {...sortProps} />
                      <SortTh label="Exchange" sortKey="exchange" {...sortProps} />
                      <SortTh label="Sector" sortKey="sector" {...sortProps} />
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-fg-subtle uppercase tracking-wide whitespace-nowrap">Price Range</th>
                      <SortTh label="Deal Size" sortKey="size" {...sortProps} />
                      <SortTh label="Status" sortKey="status" {...sortProps} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-default/50">
                    {paged.length === 0
                      ? <tr><td colSpan={8} className="px-4 py-10 text-center text-fg-subtle text-sm">No IPOs match the selected filters.</td></tr>
                      : paged.map((ipo) => (
                        <tr key={ipo.id} className="hover:bg-card-hover-bg transition-colors">
                          <td className="px-4 py-3 text-sm text-fg-muted whitespace-nowrap">{ipo.status === 'Filed' ? 'TBD' : safeDate(ipo.ipoDate, { month: 'short', day: 'numeric', year: 'numeric' }, tz)}</td>
                          <td className="px-4 py-3 text-sm text-slate-200 font-medium">{ipo.company}</td>
                          <td className="px-4 py-3"><span className="text-sky-400 text-sm font-semibold font-mono">{ipo.ticker}</span></td>
                          <td className="px-4 py-3 text-xs text-fg-subtle">{ipo.exchange || '—'}</td>
                          <td className="px-4 py-3 text-sm text-fg-muted">{ipo.sector || '—'}</td>
                          <td className="px-4 py-3 text-sm text-slate-300 font-mono">{ipo.priceRangeLow && ipo.priceRangeHigh ? `$${ipo.priceRangeLow}–$${ipo.priceRangeHigh}` : 'TBD'}</td>
                          <td className="px-4 py-3 text-sm text-slate-300 font-mono">{ipo.size ? formatMarketCap(ipo.size) : '—'}</td>
                          <td className="px-4 py-3"><span className={cn('text-xs font-medium px-2.5 py-1 rounded-full', STATUS_STYLES[ipo.status] ?? 'bg-slate-700 text-fg-muted')}>{ipo.status}</span></td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
            <Pagination page={page} totalPages={totalPages} total={sorted.length} pageSize={PAGE_SIZE} onPage={setPage} />
          </>
        )
      ) : (
        recent.length === 0 ? (
          <div className="bg-card-bg border border-default rounded-xl px-6 py-16 text-center">
            <p className="text-fg-muted">No priced IPOs available from the current data provider.</p>
          </div>
        ) : (
          <>
            <FilterBar sectors={sectors} exchanges={exchanges} statuses={[]} filterSector={filterSector} filterExchange={filterExchange} filterStatus={filterStatus} search={search} onChange={handleFilter} onSearch={q => { setSearch(q); setPage(1); }} />
            <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-default bg-card-hover-bg/50">
                    <tr>
                      <SortTh label="IPO Date" sortKey="ipoDate" {...sortProps} />
                      <SortTh label="Symbol" sortKey="ticker" {...sortProps} />
                      <SortTh label="Company" sortKey="company" {...sortProps} />
                      <SortTh label="Exchange" sortKey="exchange" {...sortProps} />
                      <SortTh label="Sector" sortKey="sector" {...sortProps} />
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-fg-subtle uppercase tracking-wide whitespace-nowrap">IPO Price</th>
                      <SortTh label="Current Price" sortKey="currentPrice" {...sortProps} />
                      <SortTh label="Return" sortKey="returnSinceIpo" {...sortProps} />
                      <SortTh label="Deal Size" sortKey="size" {...sortProps} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-default/50">
                    {paged.length === 0
                      ? <tr><td colSpan={9} className="px-4 py-10 text-center text-fg-subtle text-sm">No IPOs match the selected filters.</td></tr>
                      : paged.map((ipo) => (
                        <tr key={ipo.id} className="hover:bg-card-hover-bg transition-colors">
                          <td className="px-4 py-3 text-sm text-fg-muted whitespace-nowrap">{safeDate(ipo.ipoDate, { month: 'short', day: 'numeric', year: 'numeric' }, tz)}</td>
                          <td className="px-4 py-3"><Link href={`/stocks/${ipo.ticker}`} className="text-sky-400 hover:text-sky-300 text-sm font-semibold font-mono">{ipo.ticker}</Link></td>
                          <td className="px-4 py-3 text-sm text-slate-200">{ipo.company}</td>
                          <td className="px-4 py-3 text-xs text-fg-subtle">{ipo.exchange || '—'}</td>
                          <td className="px-4 py-3 text-sm text-fg-muted">{ipo.sector || '—'}</td>
                          <td className="px-4 py-3 text-sm text-slate-300 font-mono">{ipo.priceRangeLow && ipo.priceRangeHigh ? `$${ipo.priceRangeLow}–$${ipo.priceRangeHigh}` : '—'}</td>
                          <td className="px-4 py-3 text-sm text-white font-mono">{ipo.currentPrice ? formatCurrency(ipo.currentPrice) : '—'}</td>
                          <td className="px-4 py-3">
                            {ipo.returnSinceIpo != null
                              ? <span className={cn('text-sm font-semibold font-mono', ipo.returnSinceIpo >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]')}>{ipo.returnSinceIpo >= 0 ? '+' : ''}{formatPct(ipo.returnSinceIpo)}</span>
                              : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-300 font-mono">{ipo.size ? formatMarketCap(ipo.size) : '—'}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
            <Pagination page={page} totalPages={totalPages} total={sorted.length} pageSize={PAGE_SIZE} onPage={setPage} />
          </>
        )
      )}
    </div>
  );
}

export default function IPOsPage() {
  return (
    <Suspense>
      <IPOsPageInner />
    </Suspense>
  );
}
